// knockoutService.js
// Read-only Firestore access for Knockout Mode — the web mirror of the read half
// of FirebaseRepository_Knockout.swift. Same collections, same document-ID
// formats, same field names, same decode shapes, so the web reads exactly what
// the iOS app and the Cloud Functions write.
//
// PHASE A: reads + decoders only. Writes (session, entry, standing, registration)
// are added in Phase C once the read contract is verified against live data.
//
// Player encoding note: Knockout stores native Firestore maps/arrays — there is
// NO JSON.stringify/parse dance here (that's Gauntlet/Pick Up). The player pool
// is a map keyed by "{position}_{occurrenceIndex}" (e.g. "QB_0", "WR_0", "WR_1"),
// each key holding an array of 5 player objects — see decodePlayerPool.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { pacificDateString } from "./pacificDate";

// ─── Sport name mapping ───────────────────────────────────────────────────────
// The Firestore "sport" field is the iOS rawValue — the capitalized full name.
// The web UI uses lowercase ids; service functions accept the lowercase id and
// map to the raw value for queries and document IDs.

const SPORT_RAW = {
  football: "Football",
  baseball: "Baseball",
  basketball: "Basketball",
  hockey: "Hockey",
  soccer: "Soccer",
};

function rawSport(sportId) {
  return SPORT_RAW[sportId] || sportId;
}

// ─── Document-ID builders ─────────────────────────────────────────────────────
// Centralized so every read (and later, every write) builds IDs identically.
// One wrong separator or sport casing means reading/writing a doc nobody else
// sees, which silently breaks sync.

function poolDayDocID(poolID, dayNumber) {
  return `${poolID}_${dayNumber}`;
}

function playerPoolDocID(sportId, slot, date = new Date()) {
  return `${rawSport(sportId)}_${slot}_${pacificDateString(date)}`;
}

function standingDocID(poolID, userID) {
  return `${poolID}_${userID}`;
}

// knockout_draft_sessions and knockout_entries share the same per-user-per-day
// ID format: "{poolID}_{dayNumber}_{userID}". One builder so both stay identical.
function dayUserDocID(poolID, dayNumber, userID) {
  return `${poolID}_${dayNumber}_${userID}`;
}

function registrationDocID(userID, sportId) {
  return `${userID}_${rawSport(sportId)}`;
}

// ─── Timestamp helper ─────────────────────────────────────────────────────────
// Firestore JS returns Timestamp objects; convert to JS Date via .toDate().
// Guarded so a missing/odd field never throws during decode.

function toDate(ts) {
  return ts && typeof ts.toDate === "function" ? ts.toDate() : null;
}

// ─── Decoders ───────────────────────────────────────────────────────────────
// Each returns null when a required field is missing — the same defensive
// compactMap pattern the Swift decoders use, so a single malformed document is
// skipped rather than crashing the screen.

function decodePool(snap) {
  const d = snap.data() || {};
  if (
    d.sport == null ||
    d.poolNumber == null ||
    d.poolSlot == null ||
    d.status == null ||
    d.currentDay == null ||
    !d.startDate
  ) {
    return null;
  }
  return {
    id: snap.id,
    sport: d.sport,
    poolNumber: d.poolNumber,
    poolSlot: d.poolSlot, // "A" | "B"
    status: d.status, // "active" | "complete"
    currentDay: d.currentDay,
    totalEntrants: d.totalEntrants ?? 0,
    usersAlive: d.usersAlive ?? 0,
    winnerUserID: d.winnerUserID ?? null,
    winnerUsername: d.winnerUsername ?? null,
    startDate: toDate(d.startDate),
    endDate: toDate(d.endDate),
    // Display title, e.g. "Football Knockout #3"
    displayTitle: `${d.sport} Knockout #${d.poolNumber}`,
  };
}

function decodePoolDay(snap) {
  const d = snap.data() || {};
  if (
    d.poolID == null ||
    d.sport == null ||
    d.poolSlot == null ||
    d.dayNumber == null ||
    !d.date ||
    d.status == null
  ) {
    return null;
  }
  return {
    id: snap.id,
    poolID: d.poolID,
    sport: d.sport,
    poolSlot: d.poolSlot,
    dayNumber: d.dayNumber,
    date: toDate(d.date),
    status: d.status, // "pending" | "open" | "closed" | "finalized"
    usersAlive: d.usersAlive ?? 0,
    usersSubmitted: d.usersSubmitted ?? 0,
    usersEliminated: d.usersEliminated ?? 0,
    cutScore: d.cutScore ?? null,
    estimatedCutScore: d.estimatedCutScore ?? null,
    lastEstimateUpdatedAt: toDate(d.lastEstimateUpdatedAt),
  };
}

function decodePlayer(d) {
  if (
    d == null ||
    typeof d.name !== "string" ||
    typeof d.city !== "string" ||
    d.jerseyNumber == null ||
    d.year == null ||
    d.overallRating == null ||
    typeof d.position !== "string"
  ) {
    return null;
  }
  return {
    name: d.name,
    city: d.city,
    jerseyNumber: d.jerseyNumber,
    year: d.year,
    overallRating: d.overallRating,
    position: d.position,
  };
}

// players: { "QB_0": [Player x5], "WR_0": [...], "WR_1": [...], ... }
// Compound keys are preserved exactly — the draft (Phase D) reads
// players["{position}_{occurrenceIndex}"] walking the sport's position order.
function decodePlayerPool(snap, sportId, slot, dateString) {
  const d = snap.data() || {};
  const raw = d.players;
  if (!raw || typeof raw !== "object") return null;

  const players = {};
  for (const [slotKey, arr] of Object.entries(raw)) {
    if (!Array.isArray(arr)) continue;
    const decoded = arr.map(decodePlayer).filter(Boolean);
    if (decoded.length > 0) players[slotKey] = decoded;
  }

  return {
    id: snap.id,
    sport: rawSport(sportId),
    poolSlot: slot,
    dateString,
    generatedAt: toDate(d.generatedAt),
    players,
  };
}

function decodeStanding(snap) {
  const d = snap.data() || {};
  if (
    d.poolID == null ||
    d.sport == null ||
    d.userID == null ||
    d.username == null ||
    d.status == null ||
    !d.enteredOn
  ) {
    return null;
  }
  return {
    id: snap.id,
    poolID: d.poolID,
    sport: d.sport,
    userID: d.userID,
    username: d.username,
    status: d.status, // "alive" | "eliminated" | "winner"
    currentDayScore: d.currentDayScore ?? null,
    eliminatedOnDay: d.eliminatedOnDay ?? null,
    highestDayReached: d.highestDayReached ?? 1,
    roundsAdvanced: d.roundsAdvanced ?? 0,
    hasDraftedToday: d.hasDraftedToday ?? false,
    enteredOn: toDate(d.enteredOn),
  };
}

function decodeLeaderboardEntry(snap) {
  const d = snap.data() || {};
  if (d.userID == null || d.username == null || d.sport == null || d.totalWins == null) {
    return null;
  }
  return {
    id: snap.id,
    userID: d.userID,
    username: d.username,
    sport: d.sport,
    totalWins: d.totalWins,
    firstWinDate: toDate(d.firstWinDate),
    poolIDs: Array.isArray(d.poolIDs) ? d.poolIDs : [],
  };
}

// ─── Reads ────────────────────────────────────────────────────────────────────

// All active pools for a sport.
export async function loadActivePools(sportId) {
  const q = query(
    collection(db, "knockout_pools"),
    where("sport", "==", rawSport(sportId)),
    where("status", "==", "active")
  );
  const snap = await getDocs(q);
  return snap.docs.map(decodePool).filter(Boolean);
}

// Sports where the user is still alive in an active pool — drives the lit sport glyphs on
// the Knockout card (Roster Battles tab). Mirrors aliveSportsForKnockout in
// RosterBattlesView.swift: a sport counts as alive when it has an active pool AND the user
// either has no standing yet (Day 1, eligible to enter) OR their standing status is "alive".
// Returns a Set of lowercase sport ids. Best-effort: a per-sport read failure just omits it.
export async function loadAliveSports(userID) {
  const ids = ["football", "baseball", "basketball", "hockey", "soccer"];
  const alive = new Set();
  await Promise.all(
    ids.map(async (id) => {
      try {
        const pools = await loadActivePools(id);
        const pool = pools[0];
        if (!pool) return;
        const standing = await loadStanding(pool.id, userID);
        if (standing == null || standing.status === "alive") alive.add(id);
      } catch (_) {
        /* skip this sport */
      }
    })
  );
  return alive;
}

// Most recently completed pool for a sport (winner banner). null if none.
export async function loadLastCompletedPool(sportId) {
  const q = query(
    collection(db, "knockout_pools"),
    where("sport", "==", rawSport(sportId)),
    where("status", "==", "complete"),
    orderBy("poolNumber", "desc"),
    limit(1)
  );
  const snap = await getDocs(q);
  const first = snap.docs[0];
  return first ? decodePool(first) : null;
}

// All completed pools for a sport, newest first (Champions modal). Reuses the
// existing (sport, status, poolNumber) index — no new index required.
export async function loadCompletedPools(sportId) {
  const q = query(
    collection(db, "knockout_pools"),
    where("sport", "==", rawSport(sportId)),
    where("status", "==", "complete"),
    orderBy("poolNumber", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(decodePool).filter(Boolean);
}

// Completed pools across all five sports → { sportId: [pools] }. Five concurrent
// reads; a failing sport degrades to an empty list rather than failing the modal.
export async function loadAllCompletedPools() {
  const ids = ["football", "baseball", "basketball", "hockey", "soccer"];
  const pairs = await Promise.all(
    ids.map((id) =>
      loadCompletedPools(id)
        .then((pools) => [id, pools])
        .catch(() => [id, []])
    )
  );
  return Object.fromEntries(pairs);
}

// A single pool-day document. null if it doesn't exist yet.
export async function loadPoolDay(poolID, dayNumber) {
  const ref = doc(db, "knockout_pool_days", poolDayDocID(poolID, dayNumber));
  const snap = await getDoc(ref);
  return snap.exists() ? decodePoolDay(snap) : null;
}

// The shared player pool for a sport + slot + date. null if not generated yet.
export async function loadPlayerPool(sportId, slot, date = new Date()) {
  const dateString = pacificDateString(date);
  const ref = doc(db, "knockout_player_pools", playerPoolDocID(sportId, slot, date));
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // Mirrors the iOS "pool not found — Cloud Function may not have run yet" path.
    console.log(`🥊 knockoutService: player pool ${ref.id} not found`);
    return null;
  }
  const pool = decodePlayerPool(snap, sportId, slot, dateString);
  // Phase A contract check: confirms the date key + compound-key decode against
  // real data without any draft UI. Open the browser console to verify.
  if (pool) {
    const slotKeys = Object.keys(pool.players);
    const totalPlayers = slotKeys.reduce((n, k) => n + pool.players[k].length, 0);
    console.log(
      `🥊 knockoutService: loaded player pool ${pool.id} — ` +
        `${slotKeys.length} position slots, ${totalPlayers} players ` +
        `(keys: ${slotKeys.join(", ")})`
    );
  }
  return pool;
}

// The current user's standing in a pool. null if they haven't entered.
export async function loadStanding(poolID, userID) {
  const ref = doc(db, "knockout_standings", standingDocID(poolID, userID));
  const snap = await getDoc(ref);
  return snap.exists() ? decodeStanding(snap) : null;
}

// All standings for a pool (for the Today's Top Scores list).
export async function loadStandings(poolID) {
  const q = query(collection(db, "knockout_standings"), where("poolID", "==", poolID));
  const snap = await getDocs(q);
  return snap.docs.map(decodeStanding).filter(Boolean);
}

// Whether the user has a registration (notification opt-in) for a sport.
export async function isRegistered(userID, sportId) {
  const ref = doc(db, "knockout_registrations", registrationDocID(userID, sportId));
  const snap = await getDoc(ref);
  return snap.exists();
}

// Live count of users registered for a sport's notifications.
export async function countRegistrants(sportId) {
  const q = query(
    collection(db, "knockout_registrations"),
    where("sport", "==", rawSport(sportId))
  );
  const snap = await getDocs(q);
  return snap.size;
}

// ─── Writes ───────────────────────────────────────────────────────────────────
// PHASE C scope: registration only — the safest, fully-reversible write. The
// session / entry / standing writes land in Phase D alongside the draft screen,
// since they can only be exercised by actually drafting.
//
// Registration is purely a notification opt-in record (no registration is
// required to play). Matches FirebaseRepository_Knockout.registerForKnockout /
// unregisterFromKnockout field-for-field, so the web and iOS share one record.
//
// NOTE: web push *delivery* needs a separate FCM web token, which is out of
// scope here. We only write the opt-in document so the registrant count and the
// record stay in sync with iOS; the iOS app already writes this same doc whether
// or not the OS notification prompt was granted.

// Write (or overwrite) the user's registration for a sport.
// targetPoolID is the pool they're registering for, e.g. "Football_4".
export async function registerForKnockout(userID, sportId, targetPoolID) {
  const ref = doc(db, "knockout_registrations", registrationDocID(userID, sportId));
  await setDoc(ref, {
    userID,
    sport: rawSport(sportId),
    targetPoolID,
    registeredAt: serverTimestamp(),
  });
}

// Delete the user's registration for a sport (opt out).
export async function unregisterFromKnockout(userID, sportId) {
  const ref = doc(db, "knockout_registrations", registrationDocID(userID, sportId));
  await deleteDoc(ref);
}

// ─── Draft writes (Phase D) ───────────────────────────────────────────────────
// Mirror FirebaseRepository_Knockout writes field-for-field. The draft lifecycle:
//   startDraftSession (anchor the 90s server timer)
//     → submitEntry (write-once score)
//     → createStanding (Day 1) OR updateStandingDrafted (later days)
// The session doc is intentionally left isComplete:false after submit — the
// midnight function skips any user who already has an entry, so it's never
// overwritten with a 0 (see handleTimedOutSessions).

// Read the user's draft session for a pool day. null if none exists.
// Used for the three start paths (resume in memory / forfeit stale / fresh).
export async function loadDraftSession(poolID, dayNumber, userID) {
  const id = dayUserDocID(poolID, dayNumber, userID);
  const ref = doc(db, "knockout_draft_sessions", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const d = snap.data() || {};
  return {
    id: snap.id,
    poolID: d.poolID,
    sport: d.sport,
    userID: d.userID,
    dayNumber: d.dayNumber,
    startedAt: toDate(d.startedAt),
    expiresAt: toDate(d.expiresAt),
    isComplete: d.isComplete ?? false,
  };
}

// Write the draft session that anchors the server-side 90s timer.
// startedAt/expiresAt are real Timestamps (expiresAt is intentionally in the
// future — the notInFuture rule only applies to entry.submittedAt, not here).
export async function startDraftSession({
  poolID,
  sportId,
  userID,
  dayNumber,
  startedAt,
  expiresAt,
}) {
  const id = dayUserDocID(poolID, dayNumber, userID);
  const ref = doc(db, "knockout_draft_sessions", id);
  await setDoc(ref, {
    poolID,
    sport: rawSport(sportId),
    userID,
    dayNumber,
    startedAt: Timestamp.fromDate(startedAt),
    expiresAt: Timestamp.fromDate(expiresAt),
    isComplete: false,
  });
}

// Write the user's score entry for the day (create-only; write-once by rule).
// submittedAt uses serverTimestamp() so it can never read as "in the future".
export async function submitEntry({
  poolID,
  sportId,
  userID,
  username,
  dayNumber,
  teamRating,
  chemistryPoints,
  draftedPlayers,
  draftedPositions,
  timedOut,
}) {
  const id = dayUserDocID(poolID, dayNumber, userID);
  const ref = doc(db, "knockout_entries", id);
  await setDoc(ref, {
    poolID,
    sport: rawSport(sportId),
    userID,
    username,
    dayNumber,
    teamRating,
    chemistryPoints,
    // Strip to the six canonical fields so the shape matches iOS exactly.
    draftedPlayers: draftedPlayers.map((p) => ({
      name: p.name,
      city: p.city,
      jerseyNumber: p.jerseyNumber,
      year: p.year,
      overallRating: p.overallRating,
      position: p.position,
    })),
    draftedPositions,
    submittedAt: serverTimestamp(),
    timedOut,
  });
}

// Create the user's standing on their first (Day 1) entry. status must be
// "alive" per the rules; merge:true makes a duplicate call a no-op.
export async function createStanding({
  poolID,
  sportId,
  userID,
  username,
  currentDayScore,
  highestDayReached,
}) {
  const id = standingDocID(poolID, userID);
  const ref = doc(db, "knockout_standings", id);
  await setDoc(
    ref,
    {
      poolID,
      sport: rawSport(sportId),
      userID,
      username,
      status: "alive",
      currentDayScore,
      eliminatedOnDay: null,
      highestDayReached,
      roundsAdvanced: 0,
      hasDraftedToday: true,
      enteredOn: serverTimestamp(),
    },
    { merge: true }
  );
}

// Update an existing standing after a draft. The rules allow ONLY these two
// keys to change from the client — all other fields are Cloud-Function-managed.
export async function updateStandingDrafted(poolID, userID, score) {
  const id = standingDocID(poolID, userID);
  const ref = doc(db, "knockout_standings", id);
  await updateDoc(ref, {
    hasDraftedToday: true,
    currentDayScore: score,
  });
}
